import startCase from "lodash/startCase";
import {
  Box,
  Text,
  Image,
  Flex,
  SlideFade,
  Skeleton,
  useDisclosure,
} from "@chakra-ui/react";
import ConfirmModal from "components/ConfirmModal";
import LogoMedium from "assets/svg/logo-medium";
import SideNavCollapseIcon from "assets/svg/side-nav-collapse-icon";
import { useState } from "react";
import { navData } from "./navData";
import { useLocation, useNavigate } from "react-router-dom";
import LogoSmall from "assets/svg/logo-small";
import AvatarSmallIcon from "assets/svg/avatar-small";
import ChevronDownIcon from "assets/svg/chevron-down";
import BadgeIcon from "assets/svg/badge";
import NewAdminModal from "components/Modal/Admin/NewAdminModal";
import { isJustSuperAdmin } from "utils";
import UseInviteNewAdminModel from "models/main/useInviteNewAdminModel";
import { useAppSelector } from "app/hooks";
import { ROUTE_ACCESS, hasRouteAccess } from "utils/permissions";
import NavGroup from "./NavGroup";
import { ROUTES } from "constants/routes";

const SideNav = ({ isOpen, setIsOpen }: any) => {
  const route = useLocation();
  const navigate = useNavigate();
  const [isToggled, setIsToggled] = useState<boolean>(false);

  const { showInviteAdminModal, setShowInviteAdminModal } = UseInviteNewAdminModel();
  const { accountDetails, accountStatus } = useAppSelector(
    (state) => state.profile
  );

  const isResolvingPermissions =
    accountStatus === "idle" || accountStatus === "loading";

  const visibleNav =
    accountStatus === "error"
      ? navData
      : navData.filter((item) =>
          hasRouteAccess(accountDetails, ROUTE_ACCESS[item.link])
        );

  const isActive = (value: string) => route.pathname.includes(value);
  const user = localStorage.getItem("user");
  let parsedUser;
  if (user) {
    parsedUser = JSON.parse(user) as IProfile;
  }

  const {
    isOpen: isLogoutOpen,
    onOpen: onLogoutOpen,
    onClose: onLogoutClose,
  } = useDisclosure();

  const handleLogout = () => {
    localStorage.clear();
    navigate("/auth/login");
  };

  return (
    <Box
      height="100vh"
      paddingTop="10px"
      position="fixed"
      px={5}
      width={isOpen ? '220px' : "80px"}
      backgroundColor={'#1A0044'}
      className={isOpen ? "sidenav" : "sidenavClosed"}
      zIndex={100}
    >
      <Box
        paddingRight={isOpen ? "20px" : "0"}
        className="menuBtn"
        onClick={() => setIsOpen(!isOpen)}
      >
        <SideNavCollapseIcon />
      </Box>
      <Box mt={4}>{isOpen ? <LogoMedium /> : <LogoSmall />}</Box>
      <Box
        mt={10}
        display="flex"
        flexDirection="column"
        gap={8}
        maxHeight="calc(100vh - 280px)"
        overflowY="auto"
        sx={{
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {isResolvingPermissions
          ? navData.map((item) => (
              <Skeleton
                key={item.link}
                height="25px"
                startColor="whiteAlpha.200"
                endColor="whiteAlpha.400"
                borderRadius="4px"
              />
            ))
          : visibleNav.map((item) =>
              item.children ? (
                <NavGroup key={item.link} item={item} isOpen={isOpen} />
              ) : (
                <Box
                  key={item.link}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  role="button"
                  onClick={() => navigate(item.link)}
                >
                  <Image
                    src={item.icon}
                    alt={item.text}
                    boxSize={'25px'}
                    opacity={isActive(item.link) ? "" : "0.7"}
                  />
                  {isOpen ? (
                    <Text
                      color={isActive(item.link) ? "#E6FF55" : "#F7F7F8"}
                      opacity={isActive(item.link) ? "" : "0.7"}
                      cursor="pointer"
                      fontSize={14}
                      fontWeight={500}
                      lineHeight={"24px"}
                    >
                      {item.text}
                    </Text>
                  ) : null}
                </Box>
              )
            )}
      </Box>

      <Box
        position="absolute"
        bottom="50px"
        width="80%"
        zIndex="99"
        backgroundColor={isOpen ? "#1A0044" : 'transparent'}
        py={isOpen ? 5 : 0}
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          role="button"
          cursor="pointer"
          onClick={() => setIsToggled(!isToggled)}
        >
          <Flex gap={4} alignItems="center">
            <AvatarSmallIcon />
            {isOpen ? (
              <Box display="flex" flexDirection="column" gap={1}>
                <Text fontSize={14} color="#fff" fontWeight={500}>
                  {parsedUser?.name}
                </Text>
                <Flex alignItems="center" gap={1}>
                  <Text
                    fontSize={12}
                    color="#F7F7F8"
                    fontWeight={500}
                    opacity=".7"
                    textTransform="capitalize"
                  >
                    {startCase(parsedUser?.role)}
                  </Text>
                  <BadgeIcon />
                </Flex>
              </Box>
            ) : null}
          </Flex>
          {isOpen ? (
            <Box>
              <ChevronDownIcon />
            </Box>
          ) : null}
        </Flex>

        {isToggled && isOpen ? (
          <SlideFade offsetY="20px" in={isToggled}>
            <Box display="flex" flexDirection="column" gap={5} mt={5}>
              <Box borderBottom="1px" paddingBottom={"12px"} borderColor="#fff">
                <Text
                  fontSize={14}
                  color="#F7F7F8"
                  fontWeight={400}
                  role="button"
                  onClick={() => navigate("/main/profile")}
                >
                  Profile
                </Text>
              </Box>
              {isJustSuperAdmin(accountDetails?.admin?.role) ? (
                <Box
                  borderBottom="1px"
                  paddingBottom={"12px"}
                  borderColor="#fff"
                >
                  <Text
                    fontSize={14}
                    color="#F7F7F8"
                    fontWeight={500}
                    role="button"
                    cursor="pointer"
                    onClick={() => setShowInviteAdminModal(true)}
                  >
                    Add new admin
                  </Text>
                </Box>
              ) : null}

              {isJustSuperAdmin(accountDetails?.admin?.role) ? (
                <Box
                  borderBottom="1px"
                  paddingBottom={"12px"}
                  borderColor="#fff"
                >
                  <Text
                    fontSize={14}
                    color="#F7F7F8"
                    fontWeight={500}
                    role="button"
                    cursor="pointer"
                    onClick={() => navigate("/main/admin-users")}
                  >
                    Admin users
                  </Text>
                </Box>
              ) : null}

              {isJustSuperAdmin(accountDetails?.admin?.role) ? (
                <Box
                  borderBottom="1px"
                  paddingBottom={"12px"}
                  borderColor="#fff"
                >
                  <Text
                    fontSize={14}
                    color="#F7F7F8"
                    fontWeight={500}
                    role="button"
                    cursor="pointer"
                    onClick={() => navigate(ROUTES.FEATURE_FLAGS.path)}
                  >
                    Feature flags
                  </Text>
                </Box>
              ) : null}

              <Box
                borderBottom="1px"
                paddingBottom={"12px"}
                borderColor="#fff"
                cursor="pointer"
                role="button"
                onClick={onLogoutOpen}
              >
                <Text fontSize={14} color="#F09385" fontWeight={400}>
                  Log out
                </Text>
              </Box>
            </Box>
          </SlideFade>
        ) : null}
      </Box>
      {showInviteAdminModal && (
        <NewAdminModal
          isOpen={showInviteAdminModal}
          onClose={() => setShowInviteAdminModal(false)}
        />
      )}

      <ConfirmModal
        isOpen={isLogoutOpen}
        onClose={onLogoutClose}
        onConfirm={handleLogout}
        isDestructive
        header="Log out"
        message="Are you sure you want to log out?"
        subMessage="You will need to sign in again to get back into the dashboard."
        yesLabel="Log out"
        noLabel="Cancel"
      />
    </Box>
  );
};

export default SideNav;
